import { useEffect, useState } from "react";
import { Drawer } from "./Drawer";

type RfpAddQuestionDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly defaultSectionName: string;
  readonly sectionOptions: readonly string[];
  readonly isSubmitting: boolean;
  readonly errorMessage?: string | null;
  readonly onSubmit: (payload: { questionText: string; section: string }) => Promise<boolean>;
};

export const RfpAddQuestionDrawer = ({
  isOpen,
  onClose,
  defaultSectionName,
  sectionOptions,
  isSubmitting,
  errorMessage,
  onSubmit,
}: RfpAddQuestionDrawerProps) => {
  const [questionText, setQuestionText] = useState("");
  const [section, setSection] = useState(defaultSectionName);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuestionText("");
    setSection(defaultSectionName);
    setValidationError(null);
  }, [defaultSectionName, isOpen]);

  const handleSubmit = async () => {
    const trimmedQuestion = questionText.trim();
    const trimmedSection = section.trim();
    if (!trimmedQuestion) {
      setValidationError("Question text is required.");
      return;
    }
    if (!trimmedSection) {
      setValidationError("Section is required.");
      return;
    }
    setValidationError(null);
    const ok = await onSubmit({ questionText: trimmedQuestion, section: trimmedSection });
    if (ok) {
      onClose();
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onClose}
      title="Add question"
      className="px-0"
    >
      <div className="space-y-4 px-6 py-6">
        {(validationError || errorMessage) && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {validationError || errorMessage}
          </div>
        )}

        <div>
          <label htmlFor="add-question-section" className="mb-1 block text-sm font-medium text-gray-700">
            Section
          </label>
          <input
            id="add-question-section"
            list="rfp-section-options"
            value={section}
            onChange={(event) => setSection(event.target.value)}
            placeholder="Section name"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <datalist id="rfp-section-options">
            {sectionOptions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <p className="mt-1 text-xs text-gray-500">
            Defaults to the active section. Choose an existing section or type a new one.
          </p>
        </div>

        <div>
          <label htmlFor="add-question-text" className="mb-1 block text-sm font-medium text-gray-700">
            Question
          </label>
          <textarea
            id="add-question-text"
            value={questionText}
            onChange={(event) => setQuestionText(event.target.value)}
            rows={5}
            placeholder="Enter the question text..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="flex gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="flex-1 rounded border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
          className="flex-1 rounded bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? "Saving..." : "Add question"}
        </button>
      </div>
    </Drawer>
  );
};
